import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { clientsAPI, intelligenceAPI, competitorsAPI, searchEverywhereAPI } from '../lib/api';
import { validateCompetitorBrandName, buildCreateCompetitorPayload, competitorApiError, COMPETITOR_TYPES } from '../lib/competitorHelpers';
import { mergePrompts } from '../lib/searchEverywherePromptHelpers';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Gauge, TrendUp, MagnifyingGlass, Globe,
  ArrowUp, ArrowDown, CheckCircle, Users,
  Target, Lightbulb, ArrowsClockwise, X, Info, Warning,
  CaretRight, Eye, ChartBar, Crosshair, Plus, Trash, Play
} from '@phosphor-icons/react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { toast } from 'sonner';

/* ── helpers ─────────────────────────────────────────── */
const SC = v => v >= 60 ? 'var(--success)' : v >= 30 ? 'var(--warning)' : 'var(--error)';
const GRADES = { A: 'var(--success)', B: 'var(--info)', C: 'var(--warning)', D: 'var(--error)', F: 'var(--error)' };
const GRADES_BG = { A: 'var(--success-bg)', B: 'var(--info-bg)', C: 'var(--warning-bg)', D: 'var(--error-bg)', F: 'var(--error-bg)' };
const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '--';
const fmtDateTime = iso => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '--';
const pct = v => v != null ? `${v}%` : '--';

/* ── small reusable pieces ───────────────────────────── */
const EmptyState = ({ icon: Icon, title, subtitle, testId, action }) => (
  <div className="bg-white rounded-xl border border-[var(--border)] text-center py-14 px-6" data-testid={testId}>
    <Icon size={44} className="mx-auto mb-3 text-[var(--text-placeholder)]" weight="duotone" />
    <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
    {subtitle && <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-md mx-auto">{subtitle}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

// Batch 2 (metric label rename) -- `tooltip`, when given, renders a plain
// native title-attribute tooltip on a small (i) affordance next to the
// label. No new dependency, matches this file's own minimal component
// style -- only Mention Rate uses it today (see the call site below), but
// any other KPI whose label alone doesn't disambiguate scope can reuse it.
const KpiCard = ({ label, value, color, testId, tooltip }) => (
  <div className="bg-white rounded-xl border border-[var(--border)] p-5" data-testid={testId}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-1">
      {label}
      {tooltip && (
        <span
          title={tooltip}
          data-testid={testId ? `${testId}-tooltip` : undefined}
          className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border border-[var(--border)] text-[9px] normal-case font-normal text-[var(--text-tertiary)] cursor-help"
          aria-label={tooltip}
        >
          i
        </span>
      )}
    </p>
    <p className="text-2xl font-bold mt-1 tabular-nums" style={{ color }}>{value}</p>
  </div>
);

const Badge = ({ children, color = 'var(--info)', bg = 'var(--info-bg)' }) => (
  <span className="px-2 py-0.5 text-[11px] font-medium rounded-full inline-block" style={{ background: bg, color }}>{children}</span>
);

const PriorityBadge = ({ priority }) => {
  const map = { high: { bg: 'var(--error-bg)', color: 'var(--error)' }, medium: { bg: 'var(--warning-bg)', color: 'var(--warning)' }, low: { bg: 'var(--success-bg)', color: 'var(--success)' } };
  const s = map[priority] || map.medium;
  return <Badge color={s.color} bg={s.bg}>{priority}</Badge>;
};

/* ══════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════ */
export default function SearchEverywhereIntelPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState('');
  const [tab, setTab] = useState('overview');
  const [market, setMarket] = useState('');
  const [language, setLanguage] = useState('');

  // persisted data — zero Tavily calls on load
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [prompts, setPrompts] = useState(null);
  const [competitors, setCompetitors] = useState(null);
  const [sources, setSources] = useState(null);
  const [opportunities, setOpportunities] = useState(null);
  const [actions, setActions] = useState(null);
  const [changes, setChanges] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageRemaining, setUsageRemaining] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [execHistory, setExecHistory] = useState(null);
  const [actionImpact, setActionImpact] = useState(null);
  const [trendDays, setTrendDays] = useState(30);
  const [changeDays, setChangeDays] = useState(7);
  const [loading, setLoading] = useState(true);

  // Configured competitors (Stage 3.42) — the /v2/competitors source collection,
  // distinct from `competitors` above (derived intelligence from ai_responses).
  const [configuredCompetitors, setConfiguredCompetitors] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ brand_name: '', domain: '', competitor_type: 'primary' });
  const [addError, setAddError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Temporary manual "Sync Graph" activation action (debugging/activation only).
  // Calls the existing, unmodified competitorsAPI.syncGraph — nothing else.
  const [syncingManual, setSyncingManual] = useState(false);
  const [manualSyncResult, setManualSyncResult] = useState(null);
  const [manualSyncError, setManualSyncError] = useState('');

  // Explicit competitive execution (Stage 3.42 bridge) — distinct from SoM's Run Now.
  const [runningCompetitiveId, setRunningCompetitiveId] = useState('');
  const [competitiveRunResult, setCompetitiveRunResult] = useState(null);
  const [competitiveRunError, setCompetitiveRunError] = useState('');

  // Load clients
  useEffect(() => {
    clientsAPI.list().then(r => {
      setClients(r.data);
      if (r.data.length > 0) setSel(r.data[0].id);
    }).catch(() => toast.error('Failed to load clients'));
  }, []);

  // Load all intelligence data from persisted APIs (NO Tavily execution)
  const loadData = useCallback(async () => {
    // AF-3.20.5.4 -- a brand-new workspace with zero clients never sets
    // `sel`, so this early return used to leave `loading` stuck at its
    // initial `true` forever: the full-page spinner below never falls
    // through to the real render tree (client selector + honest
    // per-tab empty states), producing what looked like a permanently
    // blank page. `loading` must resolve to false here so the page can
    // render its "select/add a client" state instead of spinning
    // indefinitely.
    if (!sel) { setLoading(false); return; }
    setLoading(true);
    const mktParams = {};
    if (market && market !== 'all') mktParams.market = market;
    if (language && language !== 'all') mktParams.language = language;

    try {
      const [ovRes, trRes, prRes, somPrRes, coRes, srRes, opRes, acRes, chRes, usRes, urRes, diRes, ccRes] = await Promise.allSettled([
        intelligenceAPI.overview(sel, mktParams),
        intelligenceAPI.trends(sel, { days: trendDays, ...mktParams }),
        intelligenceAPI.prompts(sel),
        intelligenceAPI.prompts(sel, 'som'),
        intelligenceAPI.competitors(sel),
        intelligenceAPI.sources(sel),
        intelligenceAPI.opportunities(sel),
        intelligenceAPI.actions(sel),
        intelligenceAPI.changes(sel, { window_days: changeDays }),
        intelligenceAPI.usage(),
        intelligenceAPI.usageRemaining(),
        intelligenceAPI.diagnostics(sel),
        competitorsAPI.list(sel),
      ]);

      if (ovRes.status === 'fulfilled') setOverview(ovRes.value.data);
      if (trRes.status === 'fulfilled') setTrends(trRes.value.data);
      // General Search Everywhere prompts (prompt_type != som) and SoM prompts
      // both come from the same /v2/prompts/{clientId} route; merged here so
      // existing SoM prompts are visible (and Run Competitive-able) in this
      // tab without touching PromptService's intentional default exclusion.
      const generalPrompts = prRes.status === 'fulfilled' ? prRes.value.data : null;
      const somPromptsData = somPrRes.status === 'fulfilled' ? somPrRes.value.data : null;
      if (generalPrompts !== null || somPromptsData !== null) {
        setPrompts(mergePrompts(generalPrompts, somPromptsData));
      }
      if (coRes.status === 'fulfilled') setCompetitors(coRes.value.data);
      if (srRes.status === 'fulfilled') setSources(srRes.value.data);
      if (opRes.status === 'fulfilled') setOpportunities(opRes.value.data);
      if (acRes.status === 'fulfilled') setActions(acRes.value.data);
      if (chRes.status === 'fulfilled') setChanges(chRes.value.data);
      if (usRes.status === 'fulfilled') setUsage(usRes.value.data);
      if (urRes.status === 'fulfilled') setUsageRemaining(urRes.value.data);
      if (diRes.status === 'fulfilled') setDiagnostics(diRes.value.data);
      if (ccRes.status === 'fulfilled') setConfiguredCompetitors(ccRes.value.data);
    } catch {
      toast.error('Failed to load intelligence data');
    } finally {
      setLoading(false);
    }
  }, [sel, market, language, trendDays, changeDays]);

  useEffect(() => { loadData(); }, [loadData]);

  // Prompt history drill-down
  const loadExecHistory = async (promptId) => {
    try {
      const r = await intelligenceAPI.promptHistory(promptId);
      setExecHistory(r.data);
    } catch { toast.error('Failed to load execution history'); }
  };

  // Action impact measurement
  const loadActionImpact = async (actionId) => {
    try {
      const r = await intelligenceAPI.actionImpact(actionId);
      setActionImpact(r.data);
    } catch { toast.error('Failed to measure impact'); }
  };

  // Explicit competitive execution — the only place in the frontend that calls
  // /v2/prompts/{prompt_id}/execute-competitive. Distinct from SoM's own Run
  // Now action (SoMPromptsPage.js), which never loads db.competitors.
  const runCompetitivePrompt = async (promptId) => {
    if (runningCompetitiveId) return;
    setRunningCompetitiveId(promptId);
    setCompetitiveRunError('');
    try {
      const res = await searchEverywhereAPI.executeCompetitive(promptId);
      const data = res.data || {};
      if (data.status === 'completed') {
        setCompetitiveRunResult(data);
        toast.success('Competitive execution completed.');
        await refreshCompetitorData();
      } else {
        const msg = data.error || 'Competitive execution did not complete.';
        setCompetitiveRunError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to run competitive execution.';
      setCompetitiveRunError(msg);
      toast.error(msg);
    } finally {
      setRunningCompetitiveId('');
    }
  };

  // Refresh only the competitor configuration + derived intelligence,
  // without reloading every other tab's data.
  const refreshCompetitorData = useCallback(async () => {
    if (!sel) return;
    const [ccRes, coRes] = await Promise.allSettled([
      competitorsAPI.list(sel),
      intelligenceAPI.competitors(sel),
    ]);
    if (ccRes.status === 'fulfilled') setConfiguredCompetitors(ccRes.value.data);
    if (coRes.status === 'fulfilled') setCompetitors(coRes.value.data);
  }, [sel]);

  // Best-effort graph sync after a competitor is added/removed. A failure here
  // does not mean the competitor create/delete failed — that already succeeded.
  const syncCompetitorGraph = async () => {
    try {
      await competitorsAPI.syncGraph(sel);
    } catch {
      toast.warning('Competitor saved, but competitive intelligence may take another action or run to reflect this change.');
    }
  };

  const openAddCompetitor = () => {
    setAddForm({ brand_name: '', domain: '', competitor_type: 'primary' });
    setAddError('');
    setAddOpen(true);
  };

  const submitAddCompetitor = async () => {
    if (saving) return;
    const nameError = validateCompetitorBrandName(addForm.brand_name);
    if (nameError) {
      setAddError(nameError);
      return;
    }
    setSaving(true);
    try {
      const payload = buildCreateCompetitorPayload(sel, addForm);
      await competitorsAPI.create(sel, payload);
      toast.success('Competitor added.');
      setAddOpen(false);
      setAddForm({ brand_name: '', domain: '', competitor_type: 'primary' });
      await refreshCompetitorData();
      await syncCompetitorGraph();
    } catch (err) {
      toast.error(competitorApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteCompetitor = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await competitorsAPI.remove(deleteTarget.id);
      toast.success('Competitor removed.');
      setDeleteTarget(null);
      await refreshCompetitorData();
      await syncCompetitorGraph();
    } catch (err) {
      toast.error(competitorApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleManualSync = async () => {
    if (syncingManual) return;
    setSyncingManual(true);
    setManualSyncError('');
    setManualSyncResult(null);
    try {
      const res = await competitorsAPI.syncGraph(sel);
      const data = res.data || {};
      setManualSyncResult(data);
      toast.success(`Graph synced: ${data.competitors_processed ?? 0} competitor(s) processed, ${data.relationships_created ?? 0} relationship(s) created.`);
      await refreshCompetitorData();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to sync graph.';
      setManualSyncError(msg);
      toast.error(msg);
    } finally {
      setSyncingManual(false);
    }
  };

  const se = overview?.search_everywhere_score || {};
  const comp = se.components || {};
  const creditLimit = usageRemaining?.limit || 500;
  const creditUsed = usageRemaining?.used ?? usage?.total_credits ?? 0;
  const creditPct = Math.min((creditUsed / creditLimit) * 100, 100);

  /* ── Full-page loading ────────────────────────────── */
  if (loading && !overview) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid="se-intel-loading">
          <div className="w-6 h-6 border-2 border-[var(--info-border)] border-t-[var(--info)] rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="se-intel-page">

        {/* ══ HEADER + FILTERS ══ */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight" data-testid="se-intel-title">AI Answers &amp; Mentions</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Every AI answer Martian has recorded for your brand: who is mentioned, recommended and cited.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="se-intel-filters">
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="w-44 bg-white border-[var(--border)] text-[var(--text-primary)] text-sm" data-testid="se-intel-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent className="bg-white border-[var(--border)]">
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.display_name || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={market} onValueChange={setMarket}>
              <SelectTrigger className="w-28 bg-white border-[var(--border)] text-sm" data-testid="se-intel-market-filter"><SelectValue placeholder="Market" /></SelectTrigger>
              <SelectContent className="bg-white border-[var(--border)]">
                <SelectItem value="all">All Markets</SelectItem>
                <SelectItem value="IN">India</SelectItem>
                <SelectItem value="UAE">UAE</SelectItem>
                <SelectItem value="SA">Saudi Arabia</SelectItem>
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="UK">United Kingdom</SelectItem>
              </SelectContent>
            </Select>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-28 bg-white border-[var(--border)] text-sm" data-testid="se-intel-language-filter"><SelectValue placeholder="Language" /></SelectTrigger>
              <SelectContent className="bg-white border-[var(--border)]">
                <SelectItem value="all">All Languages</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="ar">Arabic</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ══ USAGE BAR ══ */}
        <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)]" data-testid="se-intel-usage-indicator">
          <span>Credits: <strong className="text-[var(--text-secondary)]">{creditUsed}</strong> / {creditLimit}</span>
          <div className="w-28 h-1.5 bg-[var(--neutral-bg)] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${creditPct}%`, background: creditPct > 90 ? 'var(--error)' : creditPct > 70 ? 'var(--warning)' : 'var(--info)' }} />
          </div>
          <span>{usageRemaining?.remaining ?? '--'} remaining</span>
        </div>

        {/* ══ MARKET DISCLAIMER ══ */}
        {market && market !== 'all' && (
          <div className="flex items-center gap-2 text-[11px] text-[var(--text-secondary)] bg-[var(--surface-sunken)] px-3 py-2 rounded-lg border border-[var(--border)]" data-testid="se-intel-market-disclaimer">
            <Info size={14} className="flex-shrink-0 text-[var(--text-secondary)]" />
            <span>Market filter shows the <strong>requested</strong> search context. Provider geo-localization is not guaranteed. Actual response language may differ from the requested market.</span>
          </div>
        )}

        {/* ══ NAVIGATION TABS ══ */}
        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <div className="overflow-x-auto -mx-1 px-1">
            <TabsList className="bg-[var(--neutral-bg)] rounded-lg p-1 inline-flex min-w-max" data-testid="se-intel-tabs">
              {[
                { v: 'overview', l: 'Overview', i: Gauge },
                { v: 'visibility', l: 'Visibility', i: TrendUp },
                { v: 'prompts', l: 'Prompts', i: MagnifyingGlass },
                { v: 'competitors', l: 'Competitors', i: Users },
                { v: 'sources', l: 'Sources', i: Globe },
                { v: 'opportunities', l: 'Opportunities', i: Lightbulb },
                { v: 'actions', l: 'Actions', i: Target },
                { v: 'changes', l: 'Changes', i: ArrowsClockwise },
              ].map(t => {
                const Icon = t.i;
                return (
                  <TabsTrigger
                    key={t.v}
                    value={t.v}
                    data-testid={`se-intel-tab-${t.v}`}
                    className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-[var(--text-primary)] text-[var(--text-secondary)] rounded-md text-xs gap-1.5 whitespace-nowrap"
                  >
                    <Icon size={14} /> {t.l}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {/* ═══════════════════════════════════════════
              OVERVIEW TAB
              ═══════════════════════════════════════════ */}
          <TabsContent value="overview" className="space-y-4" data-testid="se-intel-overview-content">
            {/* AF-3.20.5.4 -- `overview` is `null` (never fetched: no
                client selected, or the API call failed/returned nothing)
                as well as "fetched, zero executions" -- the old
                `overview?.total_executions === 0` check only matched the
                second case (undefined === 0 is false), so a null
                `overview` fell through into the block below that reads
                `overview.X` unconditionally and crashed. */}
            {(!overview || overview.total_executions === 0) ? (
              <EmptyState
                icon={Gauge}
                title="No intelligence data yet"
                subtitle="Create prompts and run executions to build your Search Everywhere intelligence. Start with your most important category queries. Dashboard loads consume zero Tavily credits."
                testId="se-intel-overview-empty"
              />
            ) : (
              <>
                {/* SE Score Hero */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-[var(--border)] text-center py-6" data-testid="se-score-hero">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-2">AI answers composite score</p>
                    <p className="text-5xl font-bold tabular-nums" style={{ color: GRADES[se.grade] || 'var(--text-secondary)' }}>{typeof se.score === 'number' ? se.score : '—'}</p>
                    <span className="inline-block mt-2 px-3 py-1 text-xs font-semibold rounded-full" style={{ background: GRADES_BG[se.grade], color: GRADES[se.grade] }}>
                      Grade {se.grade || '--'}
                    </span>
                    <p className="text-[10px] text-[var(--text-secondary)] mt-2 px-3" data-testid="se-score-definition">
                      A weighted index of every AI answer recorded so far, not just the latest check: mentions 30%, recommendations 20%,
                      citations 20%, source diversity 10%, competitive position 20%. Not the same measurement as Share of Model.
                    </p>
                  </div>
                  <div className="md:col-span-3 bg-white rounded-xl border border-[var(--border)] p-4" data-testid="se-score-radar">
                    <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-3">Score Components</p>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={Object.entries(comp).map(([k, v]) => ({ metric: k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), value: v }))}>
                          <PolarGrid stroke="var(--border)" />
                          <PolarAngleAxis dataKey="metric" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
                          <PolarRadiusAxis tick={{ fill: 'var(--text-secondary)', fontSize: 9 }} domain={[0, 100]} />
                          <Radar dataKey="value" stroke="var(--info)" fill="var(--info)" fillOpacity={0.12} strokeWidth={2} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* KPI Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="se-intel-kpi-grid">
                  {/* Batch 2 -- this is VisibilityService.client_overview()'s
                      cumulative rate across every ai_responses row ever
                      recorded for this client, not a point-in-time reading --
                      genuinely different from Share of Model's "latest
                      check" framing (SoMPage.js), so the label says so
                      rather than leaving "Mention Rate" ambiguous between
                      the two real, separately-computed metrics. */}
                  <KpiCard
                    label="Mention Rate (All-time)"
                    value={pct(overview.visibility_rate)}
                    color={SC(overview.visibility_rate)}
                    testId="kpi-mention-rate"
                    tooltip="Share of every AI-search response ever recorded for this brand that mentioned it — a cumulative, all-time average. Not the same measurement as Share of Model, which reflects only the most recent check."
                  />
                  <KpiCard label="Recommendation Rate" value={pct(overview.recommendation_rate)} color={SC(overview.recommendation_rate)} testId="kpi-recommendation-rate" />
                  <KpiCard label="Citation Share" value={pct(overview.citation_share)} color="var(--info)" testId="kpi-citation-share" />
                  <KpiCard label="Avg Position" value={overview.avg_position ?? '--'} color="var(--text-primary)" testId="kpi-avg-position" />
                  <KpiCard label="Source Diversity" value={`${overview.source_diversity ?? 0} domains`} color="var(--info)" testId="kpi-source-diversity" />
                  <KpiCard label="Total Citations" value={overview.total_citations ?? 0} color="var(--info)" testId="kpi-total-citations" />
                  <KpiCard label="Brand Citations" value={overview.brand_citations ?? 0} color="var(--success)" testId="kpi-brand-citations" />
                  <KpiCard label="Executions" value={overview.total_executions ?? 0} color="var(--text-secondary)" testId="kpi-executions" />
                </div>

                {/* Diagnostics summary (from Phase 2E) */}
                {diagnostics && diagnostics.length > 0 && diagnostics[0]?.type !== 'insufficient_data' && (
                  <div className="bg-white rounded-xl border border-[var(--border)] p-4" data-testid="se-intel-diagnostics-summary">
                    <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-3">Diagnostics</p>
                    <div className="space-y-2">
                      {diagnostics.map((d, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[var(--warning-bg)] border border-[var(--warning-border)]">
                          <Warning size={16} className="flex-shrink-0 text-[var(--warning)] mt-0.5" weight="fill" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[var(--warning)] capitalize">{d.type?.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-[var(--warning)] mt-0.5">
                              Confidence: {Math.round((d.confidence || 0) * 100)}%
                              {d.evidence && Object.entries(d.evidence).map(([k, v]) => (
                                <span key={k} className="ml-3">{k.replace(/_/g, ' ')}: <strong>{v}</strong></span>
                              ))}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ═══════════════════════════════════════════
              VISIBILITY TAB
              ═══════════════════════════════════════════ */}
          <TabsContent value="visibility" className="space-y-4" data-testid="se-intel-visibility-content">
            <div className="flex items-center gap-2 mb-1" data-testid="se-intel-trend-days">
              {[7, 30, 90].map(d => (
                <button
                  key={d}
                  onClick={() => setTrendDays(d)}
                  data-testid={`trend-days-${d}`}
                  className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${trendDays === d ? 'bg-white shadow-sm text-[var(--text-primary)] border border-[var(--border)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]'}`}
                >
                  {d}D
                </button>
              ))}
            </div>
            {!trends || trends.length === 0 ? (
              <EmptyState
                icon={TrendUp}
                title="Not enough historical data yet"
                subtitle="Run prompt executions over multiple days to see visibility trends. At least two data points are needed."
                testId="se-intel-visibility-empty"
              />
            ) : (
              <>
                <div className="bg-white rounded-xl border border-[var(--border)] p-4" data-testid="se-intel-visibility-chart">
                  <p className="text-sm font-semibold text-[var(--text-primary)] mb-3">Visibility Trend ({trendDays} days)</p>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                        <XAxis dataKey="date" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                        <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)' }} />
                        <Line type="monotone" dataKey="mention_rate" stroke="var(--info)" strokeWidth={2} dot={{ r: 3 }} name="Mention %" />
                        <Line type="monotone" dataKey="recommendation_rate" stroke="var(--success)" strokeWidth={2} dot={{ r: 3 }} name="Recommendation %" />
                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Trend data table */}
                <div className="bg-white rounded-xl border border-[var(--border)] overflow-x-auto" data-testid="se-intel-visibility-table">
                  <table className="w-full text-sm min-w-[480px]">
                    <thead>
                      <tr className="border-b border-[var(--border-light)] bg-[var(--surface-sunken)]">
                        <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Date</th>
                        <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Executions</th>
                        <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Mention %</th>
                        <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Recommend %</th>
                        <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Avg Position</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trends.map(t => (
                        <tr key={t.date} className="border-b border-[var(--border-light)] hover:bg-[var(--surface-sunken)]">
                          <td className="p-3 text-[var(--text-primary)] font-medium">{t.date}</td>
                          <td className="p-3 text-right tabular-nums text-[var(--text-secondary)]">{t.executions}</td>
                          <td className="p-3 text-right tabular-nums" style={{ color: SC(t.mention_rate) }}>{t.mention_rate}%</td>
                          <td className="p-3 text-right tabular-nums" style={{ color: SC(t.recommendation_rate) }}>{t.recommendation_rate}%</td>
                          <td className="p-3 text-right tabular-nums text-[var(--text-secondary)]">{t.avg_position ?? '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          {/* ═══════════════════════════════════════════
              PROMPTS TAB
              ═══════════════════════════════════════════ */}
          <TabsContent value="prompts" className="space-y-4" data-testid="se-intel-prompts-content">
            {!prompts || prompts.length === 0 ? (
              <EmptyState
                icon={MagnifyingGlass}
                title="No prompts tracked yet"
                subtitle="Create prompts via the API to start tracking AI visibility. Prompts define the category queries you want to monitor."
                testId="se-intel-prompts-empty"
              />
            ) : (
              <div className="bg-white rounded-xl border border-[var(--border)] overflow-x-auto" data-testid="se-intel-prompts-table">
                <table className="w-full text-sm min-w-[560px]">
                  <thead>
                    <tr className="border-b border-[var(--border-light)] bg-[var(--surface-sunken)]">
                      <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Prompt</th>
                      <th className="text-center p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Market</th>
                      <th className="text-center p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Language</th>
                      <th className="text-center p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Type</th>
                      <th className="text-center p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Active</th>
                      <th className="p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prompts.map(p => (
                      <tr key={p.id} className="border-b border-[var(--border-light)] hover:bg-[var(--surface-sunken)] transition-colors">
                        <td className="p-3 text-[var(--text-primary)] font-medium max-w-xs truncate">{p.prompt_text}</td>
                        <td className="p-3 text-center text-[var(--text-secondary)]">{p.market || '--'}</td>
                        <td className="p-3 text-center text-[var(--text-secondary)]">{p.language || '--'}</td>
                        <td className="p-3 text-center"><Badge>{p.prompt_type}</Badge></td>
                        <td className="p-3 text-center">{p.active ? <CheckCircle size={16} className="inline text-[var(--success)]" weight="fill" /> : <span className="text-[var(--text-secondary)]">Inactive</span>}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => loadExecHistory(p.id)}
                              data-testid={`prompt-history-btn-${p.id}`}
                              className="inline-flex items-center gap-1 text-xs text-[var(--info)] hover:text-[var(--info)] font-medium transition-colors"
                            >
                              <Eye size={14} /> History
                            </button>
                            <button
                              onClick={() => runCompetitivePrompt(p.id)}
                              disabled={!!runningCompetitiveId}
                              data-testid={`run-competitive-btn-${p.id}`}
                              title="Run through the competitive execution pipeline (uses your configured competitors)"
                              className="inline-flex items-center gap-1 text-xs text-[var(--accent)] hover:text-[var(--accent-hover)] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Play size={14} weight={runningCompetitiveId === p.id ? 'fill' : 'regular'} />
                              {runningCompetitiveId === p.id ? 'Running…' : 'Run Competitive'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Competitive Execution Result */}
            {(competitiveRunResult || competitiveRunError) && (
              <div className="bg-white rounded-xl border border-[var(--accent-border)] p-4 animate-in fade-in slide-in-from-bottom-2 duration-200" data-testid="se-intel-competitive-run-result">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-[var(--text-primary)]">Competitive Execution Result</p>
                  <button
                    onClick={() => { setCompetitiveRunResult(null); setCompetitiveRunError(''); }}
                    className="p-1.5 rounded-lg hover:bg-[var(--neutral-bg)] transition-colors"
                    data-testid="close-competitive-run-result"
                  >
                    <X size={16} className="text-[var(--text-secondary)]" />
                  </button>
                </div>
                {competitiveRunError ? (
                  <p className="text-sm text-[var(--error)]" data-testid="se-intel-competitive-run-error">{competitiveRunError}</p>
                ) : (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--text-secondary)]">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${competitiveRunResult.intelligence?.brand_mentioned ? 'bg-[var(--success-bg)] text-[var(--success)]' : 'bg-[var(--neutral-bg)] text-[var(--text-secondary)]'}`}>
                        {competitiveRunResult.intelligence?.brand_mentioned ? 'Brand Mentioned' : 'Brand Not Mentioned'}
                      </span>
                      <span>Competitors checked: <strong className="text-[var(--text-primary)]">{competitiveRunResult.intelligence?.competitor_results?.length ?? 0}</strong></span>
                      <span>Competitors mentioned: <strong className="text-[var(--text-primary)]">{competitiveRunResult.intelligence?.competitors_mentioned ?? 0}</strong></span>
                    </div>
                    {competitiveRunResult.intelligence?.competitor_results?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {competitiveRunResult.intelligence.competitor_results.map(c => (
                          <span
                            key={c.name}
                            className={`px-2 py-0.5 text-[11px] rounded-full ${c.mentioned ? 'bg-[var(--warning-bg)] text-[var(--warning)]' : 'bg-[var(--neutral-bg)] text-[var(--text-secondary)]'}`}
                          >
                            {c.name} — {c.mentioned ? 'mentioned' : 'not mentioned'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Execution History Drill-down */}
            {execHistory && (
              <div className="bg-white rounded-xl border border-[var(--border)] p-4 animate-in fade-in slide-in-from-bottom-2 duration-200" data-testid="se-intel-exec-history">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-semibold text-[var(--text-primary)]">Execution History</p>
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">{execHistory.prompt_text} ({execHistory.executions} executions)</p>
                  </div>
                  <button onClick={() => setExecHistory(null)} className="p-1.5 rounded-lg hover:bg-[var(--neutral-bg)] transition-colors" data-testid="close-exec-history">
                    <X size={16} className="text-[var(--text-secondary)]" />
                  </button>
                </div>
                {execHistory.history?.length === 0 ? (
                  <p className="text-sm text-[var(--text-secondary)] py-4 text-center">No executions yet for this prompt.</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {execHistory.history.map(h => (
                      <div key={h.response_id} className="p-3 rounded-lg border border-[var(--border-light)] hover:border-[var(--border)] transition-colors" data-testid={`exec-entry-${h.response_id}`}>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--text-secondary)]">
                          <span className="text-[var(--text-primary)] font-medium">{fmtDateTime(h.created_at)}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${h.brand_mentioned ? 'bg-[var(--success-bg)] text-[var(--success)]' : 'bg-[var(--neutral-bg)] text-[var(--text-secondary)]'}`}>
                            {h.brand_mentioned ? 'Cited' : 'Not Cited'}
                          </span>
                          {h.brand_position && <span className="text-[var(--text-primary)]">Position #{h.brand_position}</span>}
                          {h.brand_recommended === true && <Badge color="var(--success)" bg="var(--success-bg)">Recommended</Badge>}
                          {h.brand_recommended === false && <Badge color="var(--text-secondary)" bg="var(--neutral-bg)">Not Recommended</Badge>}
                        </div>
                        {/* Market vs Language disclosure */}
                        <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px] text-[var(--text-secondary)]">
                          <span>Requested Market: <strong className="text-[var(--text-secondary)]">{h.market || 'Not specified'}</strong></span>
                          <span>Response Language: <strong className="text-[var(--text-secondary)]">{h.language || 'Unknown'}</strong></span>
                          <span>Provider: <strong className="text-[var(--text-secondary)]">{h.provider || 'Unknown'}</strong></span>
                          {h.market && h.language && h.market !== h.language && (
                            <span className="text-[var(--warning)]">Market/language mismatch</span>
                          )}
                        </div>
                        {h.competitors?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {h.competitors.filter(c => c.mentioned).map(c => (
                              <span key={c.name} className="px-2 py-0.5 text-[11px] rounded-full bg-[var(--warning-bg)] text-[var(--warning)]">{c.name} {c.position ? `#${c.position}` : ''}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* ═══════════════════════════════════════════
              COMPETITORS TAB
              ═══════════════════════════════════════════ */}
          <TabsContent value="competitors" className="space-y-4" data-testid="se-intel-competitors-content">
            {(!configuredCompetitors || configuredCompetitors.length === 0) ? (
              <EmptyState
                icon={Users}
                title="No competitors configured yet"
                subtitle="Add competitors to start tracking competitive visibility and recommendations."
                testId="se-intel-competitors-empty"
                action={
                  <Button onClick={openAddCompetitor} data-testid="add-competitor-btn-empty" size="sm" className="bg-[var(--info)] hover:opacity-90 text-white">
                    <Plus size={14} className="mr-1.5" /> Add Competitor
                  </Button>
                }
              />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-[var(--text-primary)]">Competitors</p>
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">Manage the competitors you track for Search Everywhere visibility.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleManualSync}
                      disabled={syncingManual}
                      data-testid="sync-graph-btn"
                      size="sm"
                      variant="outline"
                      title="Manually sync your configured competitors into the graph (temporary activation action)"
                      className="border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowsClockwise size={14} className={`mr-1.5 ${syncingManual ? 'animate-spin' : ''}`} />
                      {syncingManual ? 'Syncing...' : 'Sync Graph'}
                    </Button>
                    <Button onClick={openAddCompetitor} data-testid="add-competitor-btn" size="sm" className="bg-[var(--info)] hover:opacity-90 text-white">
                      <Plus size={14} className="mr-1.5" /> Add Competitor
                    </Button>
                  </div>
                </div>

                {(manualSyncResult || manualSyncError) && (
                  <div className="bg-white rounded-xl border border-[var(--border)] p-3" data-testid="sync-graph-result">
                    {manualSyncError ? (
                      <p className="text-sm text-[var(--error)]" data-testid="sync-graph-error">{manualSyncError}</p>
                    ) : (
                      <p className="text-sm text-[var(--text-secondary)]" data-testid="sync-graph-success">
                        Graph synced: <strong className="text-[var(--text-primary)]">{manualSyncResult.competitors_processed ?? 0}</strong> competitor(s) processed,{' '}
                        <strong className="text-[var(--text-primary)]">{manualSyncResult.relationships_created ?? 0}</strong> relationship(s) created,{' '}
                        <strong className="text-[var(--text-primary)]">{manualSyncResult.relationships_reused ?? 0}</strong> reused.
                      </p>
                    )}
                  </div>
                )}

                {(!competitors || competitors.length === 0) ? (
                  <div className="bg-white rounded-xl border border-[var(--border)] text-center py-8 px-6" data-testid="se-intel-competitors-visibility-pending">
                    <p className="text-sm text-[var(--text-secondary)]">Competitive visibility data will appear here once prompt executions run against your configured competitors.</p>
                  </div>
                ) : (
                  <>
                    {/* Brand vs Competitors chart */}
                    <div className="bg-white rounded-xl border border-[var(--border)] p-4" data-testid="se-intel-competitors-chart">
                      <p className="text-sm font-semibold text-[var(--text-primary)] mb-3">Brand vs Competitors</p>
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={[
                            { name: 'Your Brand', visibility: overview?.visibility_rate || 0, recommendation: overview?.recommendation_rate || 0 },
                            ...competitors.map(c => ({ name: c.name, visibility: c.visibility_rate, recommendation: c.recommendation_rate }))
                          ]}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                            <XAxis dataKey="name" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                            <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 12, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)' }} />
                            <Bar dataKey="visibility" fill="var(--info)" radius={[4, 4, 0, 0]} name="Visibility %" />
                            <Bar dataKey="recommendation" fill="var(--success)" radius={[4, 4, 0, 0]} name="Recommendation %" />
                            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Competitor table */}
                    <div className="bg-white rounded-xl border border-[var(--border)] overflow-x-auto" data-testid="se-intel-competitors-table">
                      <table className="w-full text-sm min-w-[400px]">
                        <thead>
                          <tr className="border-b border-[var(--border-light)] bg-[var(--surface-sunken)]">
                            <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Competitor</th>
                            <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Visibility</th>
                            <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Recommendation</th>
                            <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Gap vs You</th>
                          </tr>
                        </thead>
                        <tbody>
                          {competitors.map(c => {
                            const gap = (c.visibility_rate || 0) - (overview?.visibility_rate || 0);
                            return (
                              <tr key={c.name} className="border-b border-[var(--border-light)] hover:bg-[var(--surface-sunken)]" data-testid={`competitor-row-${c.name}`}>
                                <td className="p-3 text-[var(--text-primary)] font-medium">{c.name}</td>
                                <td className="p-3 text-right font-mono tabular-nums" style={{ color: SC(c.visibility_rate) }}>{c.visibility_rate}%</td>
                                <td className="p-3 text-right font-mono tabular-nums" style={{ color: SC(c.recommendation_rate) }}>{c.recommendation_rate}%</td>
                                <td className="p-3 text-right font-mono tabular-nums">
                                  <span className={gap > 0 ? 'text-[var(--error)]' : 'text-[var(--success)]'}>{gap > 0 ? '+' : ''}{gap.toFixed(1)}%</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* Configured competitors management */}
                <div className="bg-white rounded-xl border border-[var(--border)] overflow-x-auto" data-testid="configured-competitors-list">
                  <div className="p-4 border-b border-[var(--border-light)]">
                    <p className="text-sm font-semibold text-[var(--text-primary)]">Configured Competitors</p>
                  </div>
                  <table className="w-full text-sm min-w-[420px]">
                    <thead>
                      <tr className="border-b border-[var(--border-light)] bg-[var(--surface-sunken)]">
                        <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Brand</th>
                        <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Domain</th>
                        <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Type</th>
                        <th className="p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {configuredCompetitors.map(c => (
                        <tr key={c.id} className="border-b border-[var(--border-light)] hover:bg-[var(--surface-sunken)]" data-testid={`configured-competitor-row-${c.id}`}>
                          <td className="p-3 text-[var(--text-primary)] font-medium">{c.brand_name}</td>
                          <td className="p-3 text-[var(--text-secondary)]">{c.domain || '--'}</td>
                          <td className="p-3"><Badge>{c.competitor_type}</Badge></td>
                          <td className="p-3">
                            <button
                              onClick={() => setDeleteTarget(c)}
                              data-testid={`delete-competitor-btn-${c.id}`}
                              className="inline-flex items-center gap-1 text-xs text-[var(--error)] hover:text-[var(--error)] font-medium transition-colors"
                            >
                              <Trash size={14} /> Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          {/* ═══════════════════════════════════════════
              SOURCES TAB
              ═══════════════════════════════════════════ */}
          <TabsContent value="sources" className="space-y-4" data-testid="se-intel-sources-content">
            {!sources || sources.length === 0 ? (
              <EmptyState
                icon={Globe}
                title="No source intelligence available"
                subtitle="Sources are discovered when prompt executions capture citations. Run more executions to build source intelligence."
                testId="se-intel-sources-empty"
              />
            ) : (
              <div className="bg-white rounded-xl border border-[var(--border)] overflow-x-auto" data-testid="se-intel-sources-table">
                <div className="p-4 border-b border-[var(--border-light)]">
                  <p className="text-sm font-semibold text-[var(--text-primary)]">Source Influence</p>
                  <p className="text-xs text-[var(--text-secondary)] mt-0.5">Domains ranked by appearance frequency in AI responses</p>
                </div>
                <table className="w-full text-sm min-w-[480px]">
                  <thead>
                    <tr className="border-b border-[var(--border-light)] bg-[var(--surface-sunken)]">
                      <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">#</th>
                      <th className="text-left p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Domain</th>
                      <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Appearances</th>
                      <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Brand Citations</th>
                      <th className="text-right p-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Unique Prompts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((s, i) => (
                      <tr key={s.domain} className="border-b border-[var(--border-light)] hover:bg-[var(--surface-sunken)]" data-testid={`source-row-${i}`}>
                        <td className="p-3 text-[var(--text-secondary)] font-mono text-xs">{i + 1}</td>
                        <td className="p-3 text-[var(--info)] font-medium">{s.domain}</td>
                        <td className="p-3 text-right font-mono tabular-nums text-[var(--text-primary)]">{s.appearances}</td>
                        <td className="p-3 text-right font-mono tabular-nums text-[var(--success)]">{s.brand_citations}</td>
                        <td className="p-3 text-right font-mono tabular-nums text-[var(--text-secondary)]">{s.unique_prompts}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* ═══════════════════════════════════════════
              OPPORTUNITIES TAB
              ═══════════════════════════════════════════ */}
          <TabsContent value="opportunities" className="space-y-3" data-testid="se-intel-opportunities-content">
            {!opportunities || opportunities.length === 0 ? (
              <EmptyState
                icon={Lightbulb}
                title="No opportunities identified"
                subtitle="Opportunities are generated from diagnostics when enough execution data exists. Run more prompts to discover optimization opportunities."
                testId="se-intel-opportunities-empty"
              />
            ) : (
              <>
                <p className="text-xs text-[var(--text-secondary)]">{opportunities.length} opportunities identified</p>
                {opportunities.map((o, i) => (
                  <div key={o.id || i} className="bg-white rounded-xl border border-[var(--border)] p-4" data-testid={`opportunity-card-${i}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <PriorityBadge priority={o.priority} />
                        <span className="text-xs font-medium text-[var(--text-secondary)] capitalize">{o.type?.replace(/_/g, ' ')}</span>
                      </div>
                      <span className="text-xs text-[var(--text-secondary)]">Severity: {o.severity}</span>
                    </div>
                    {o.evidence && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {Object.entries(o.evidence).map(([k, v]) => (
                          <span key={k} className="text-[11px] text-[var(--text-secondary)] bg-[var(--surface-sunken)] px-2 py-0.5 rounded">
                            {k.replace(/_/g, ' ')}: <strong>{typeof v === 'number' ? v.toFixed?.(1) ?? v : v}</strong>
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-sm text-[var(--text-primary)] font-medium flex items-start gap-2">
                      <CaretRight size={14} className="flex-shrink-0 mt-0.5 text-[var(--info)]" />
                      {o.recommended_action}
                    </p>
                  </div>
                ))}
              </>
            )}
          </TabsContent>

          {/* ═══════════════════════════════════════════
              ACTIONS TAB
              ═══════════════════════════════════════════ */}
          <TabsContent value="actions" className="space-y-3" data-testid="se-intel-actions-content">
            {!actions || actions.length === 0 ? (
              <EmptyState
                icon={Target}
                title="No optimization actions created"
                subtitle="Actions are created from opportunities or manually to track optimization efforts and measure their observed impact over time."
                testId="se-intel-actions-empty"
              />
            ) : (
              <>
                <p className="text-xs text-[var(--text-secondary)]">{actions.length} actions tracked</p>
                {actions.map(a => (
                  <div key={a.id} className="bg-white rounded-xl border border-[var(--border)] p-4" data-testid={`action-card-${a.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-[var(--text-primary)]">{a.description || a.action_type || 'Untitled action'}</span>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${a.status === 'completed' ? 'bg-[var(--success-bg)] text-[var(--success)]' : a.status === 'started' ? 'bg-[var(--info-bg)] text-[var(--info)]' : 'bg-[var(--neutral-bg)] text-[var(--text-secondary)]'}`}>
                          {a.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--text-secondary)]">
                      {a.baseline_visibility != null && <span>Baseline visibility: <strong>{a.baseline_visibility}%</strong></span>}
                      {a.baseline_recommendation_rate != null && <span>Baseline recommendation: <strong>{a.baseline_recommendation_rate}%</strong></span>}
                      {a.baseline_citation_share != null && <span>Baseline citation share: <strong>{a.baseline_citation_share}%</strong></span>}
                      <span>Created: {fmtDate(a.created_at)}</span>
                      {a.followup_window_days && <span>Follow-up: {a.followup_window_days} days</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => loadActionImpact(a.id)}
                        data-testid={`action-impact-btn-${a.id}`}
                        className="text-xs h-7"
                      >
                        <ChartBar size={14} className="mr-1" /> Measure Impact
                      </Button>
                    </div>
                    <p className="text-[10px] text-[var(--text-secondary)] mt-2 italic">Observed change after action -- not causal attribution</p>
                  </div>
                ))}

                {/* Impact panel */}
                {actionImpact && (
                  <div className="bg-white rounded-xl border border-[var(--info-border)] p-4 animate-in fade-in slide-in-from-bottom-2 duration-200" data-testid="se-intel-action-impact">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-[var(--text-primary)]">Observed Impact</p>
                      <button onClick={() => setActionImpact(null)} className="p-1 rounded hover:bg-[var(--neutral-bg)]" data-testid="close-action-impact"><X size={14} className="text-[var(--text-secondary)]" /></button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {['visibility', 'recommendation', 'citation_share'].map(metric => {
                        const change = actionImpact.change?.[metric];
                        return (
                          <div key={metric} className="text-center p-3 rounded-lg bg-[var(--surface-sunken)]">
                            <p className="text-[11px] text-[var(--text-secondary)] capitalize mb-1">{metric.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-[var(--text-secondary)]">
                              {actionImpact.baseline?.[metric] ?? '--'}%
                              <CaretRight size={10} className="inline mx-1" />
                              {actionImpact.current?.[metric] ?? '--'}%
                            </p>
                            <p className={`text-lg font-bold tabular-nums mt-1 ${change > 0 ? 'text-[var(--success)]' : change < 0 ? 'text-[var(--error)]' : 'text-[var(--text-secondary)]'}`}>
                              {change > 0 ? '+' : ''}{change ?? 0}%
                            </p>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[var(--text-secondary)] mt-3 italic text-center">{actionImpact.note}</p>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ═══════════════════════════════════════════
              CHANGES TAB
              ═══════════════════════════════════════════ */}
          <TabsContent value="changes" className="space-y-3" data-testid="se-intel-changes-content">
            <div className="flex items-center gap-2 mb-1" data-testid="se-intel-change-window">
              {[7, 14, 30].map(d => (
                <button
                  key={d}
                  onClick={() => setChangeDays(d)}
                  data-testid={`change-window-${d}`}
                  className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${changeDays === d ? 'bg-white shadow-sm text-[var(--text-primary)] border border-[var(--border)]' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]'}`}
                >
                  {d}-day window
                </button>
              ))}
            </div>
            {!changes || changes.length === 0 ? (
              <EmptyState
                icon={ArrowsClockwise}
                title="No visibility changes detected"
                subtitle={`Changes are detected by comparing ${changeDays}-day windows. Run executions over multiple periods to detect changes.`}
                testId="se-intel-changes-empty"
              />
            ) : (
              <>
                <p className="text-xs text-[var(--text-secondary)]">{changes.length} change events detected in {changeDays}-day window</p>
                {changes.map((c, i) => (
                  <div key={i} className="bg-white rounded-xl border border-[var(--border)] p-4" data-testid={`change-card-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {(c.delta > 0 || c.direction === 'increase') ? (
                        <ArrowUp size={18} className="text-[var(--success)]" weight="bold" />
                      ) : (
                        <ArrowDown size={18} className="text-[var(--error)]" weight="bold" />
                      )}
                      <span className="text-sm font-medium text-[var(--text-primary)] capitalize">{c.type?.replace(/_/g, ' ')}</span>
                      {c.competitor && <Badge color="var(--warning)" bg="var(--warning-bg)">{c.competitor}</Badge>}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--text-secondary)]">
                      {c.previous_rate != null && <span>Previous: <strong>{c.previous_rate}%</strong></span>}
                      {c.previous != null && c.previous_rate == null && <span>Previous: <strong>{c.previous}</strong></span>}
                      {c.current_rate != null && <span>Current: <strong>{c.current_rate}%</strong></span>}
                      {c.current != null && c.current_rate == null && <span>Current: <strong>{c.current}</strong></span>}
                      {c.delta != null && (
                        <span className={`font-semibold ${c.delta > 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                          {c.delta > 0 ? '+' : ''}{c.delta}{c.current_rate != null ? '%' : ''}
                        </span>
                      )}
                      {c.window_days && <span className="text-[var(--text-secondary)]">({c.window_days}-day window)</span>}
                    </div>
                  </div>
                ))}
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* ═══════════════════════════════════════════
            ADD COMPETITOR DIALOG
            ═══════════════════════════════════════════ */}
        <Dialog open={addOpen} onOpenChange={(open) => { if (!saving) setAddOpen(open); }}>
          <DialogContent className="bg-white border-[var(--border)]" data-testid="add-competitor-dialog">
            <DialogHeader>
              <DialogTitle className="text-[var(--text-primary)]">Add Competitor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="block text-[11px] text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Brand name *</label>
                <Input
                  data-testid="competitor-brand-name-input"
                  value={addForm.brand_name}
                  onChange={(e) => { setAddForm({ ...addForm, brand_name: e.target.value }); setAddError(''); }}
                  placeholder="Competitor brand name"
                  className="bg-white border-[var(--border)] text-[var(--text-primary)]"
                />
                {addError && <p className="text-xs text-[var(--error)] mt-1" data-testid="competitor-brand-name-error">{addError}</p>}
              </div>
              <div>
                <label className="block text-[11px] text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Domain (optional)</label>
                <Input
                  data-testid="competitor-domain-input"
                  value={addForm.domain}
                  onChange={(e) => setAddForm({ ...addForm, domain: e.target.value })}
                  placeholder="competitor.com"
                  className="bg-white border-[var(--border)] text-[var(--text-primary)]"
                />
              </div>
              <div>
                <label className="block text-[11px] text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Competitor type</label>
                <Select value={addForm.competitor_type} onValueChange={(v) => setAddForm({ ...addForm, competitor_type: v })}>
                  <SelectTrigger data-testid="competitor-type-select" className="bg-white border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-[var(--border)]">
                    {COMPETITOR_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid="save-competitor-btn"
                onClick={submitAddCompetitor}
                disabled={saving}
                className="w-full bg-[var(--info)] hover:opacity-90 text-white"
              >
                {saving ? 'Saving…' : 'Add Competitor'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ═══════════════════════════════════════════
            DELETE COMPETITOR CONFIRMATION
            ═══════════════════════════════════════════ */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}>
          <AlertDialogContent className="bg-white border-[var(--border)]" data-testid="delete-competitor-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[var(--text-primary)]">Remove this competitor?</AlertDialogTitle>
              <AlertDialogDescription className="text-[var(--text-secondary)]">
                {deleteTarget?.brand_name} will no longer be tracked for competitive visibility. This can be re-added later.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-competitor-cancel" disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="delete-competitor-confirm-btn"
                disabled={deleting}
                onClick={confirmDeleteCompetitor}
                className="bg-[var(--error)] hover:opacity-90 text-white"
              >
                {deleting ? 'Removing…' : 'Remove'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
